import type {
    ModelKey,
    PlanConfig,
    ReportConfig,
    ReportConfigurationClientLookup,
    ReportConfigurationReportDto,
    ReportConfigurationReportFolderDto,
    ReportConfigurationReportPlanDto,
    ReportConfigurationState,
    ReportFolder,
} from "../types";
import { clone, makeReportGlobalFilters } from "../logic/metrics";

type MakeReport = (id: string, label: string, models: ModelKey[], seedMetrics?: boolean) => ReportConfig;

export function findExistingPlanForClient(plans: PlanConfig[], client: ReportConfigurationClientLookup) {
    const clientId = String(client.clientId);
    const identifier = client.healthPlanIdentifier.toLowerCase();
    const displayName = client.displayName.toLowerCase();
    const companyName = client.companyName.toLowerCase();

    return plans.find((plan) => (
        String(plan.clientId || "") === clientId
        || plan.id === clientId
        || plan.id.toLowerCase() === identifier
        || plan.name.toLowerCase() === identifier
        || plan.name.toLowerCase() === displayName
        || plan.clientName.toLowerCase() === companyName
    ));
}

export function buildPlanFromClient(client: ReportConfigurationClientLookup, existing?: PlanConfig): PlanConfig {
    const displayName = client.displayName || client.healthPlanIdentifier || client.companyName || `Client ${client.clientId}`;
    const companyName = client.companyName || displayName;

    return {
        id: String(client.clientId),
        clientId: client.clientId,
        name: displayName,
        clientName: companyName,
        reportFolders: existing?.reportFolders?.length ? clone(existing.reportFolders) : [],
    };
}

export function mergeClientLookupsIntoData(current: ReportConfigurationState, clients: ReportConfigurationClientLookup[], preserveExisting: boolean): ReportConfigurationState {
    const seenClientIds = new Set<number>();
    const plans = clients
        .filter((client) => {
            if (seenClientIds.has(client.clientId)) return false;
            seenClientIds.add(client.clientId);
            return true;
        })
        .map((client) => buildPlanFromClient(client, preserveExisting ? findExistingPlanForClient(current.plans, client) : undefined));

    if (!plans.length) {
        return current;
    }

    return {
        ...current,
        plans,
        changes: preserveExisting ? current.changes : [],
    };
}

export function reportFolderUiId(reportFolderId: number) {
    return `report_folder_${reportFolderId}`;
}

export function reportUiId(reportId: number) {
    return `report_${reportId}`;
}

export function normalizeMatchText(value?: string | null) {
    return String(value || "").trim().toLowerCase();
}

export function findExistingFolderForDto(existingFolders: ReportFolder[], dto: ReportConfigurationReportFolderDto) {
    const folderKey = normalizeMatchText(dto.folderKey);
    const label = normalizeMatchText(dto.label);
    return existingFolders.find((folder) => (
        folder.reportFolderId === dto.reportFolderId
        || (!!folderKey && normalizeMatchText(folder.folderKey) === folderKey)
        || normalizeMatchText(folder.label) === label
    ));
}

export function findExistingReportForDto(existingFolder: ReportFolder | undefined, allExistingFolders: ReportFolder[], dto: ReportConfigurationReportDto) {
    const reportKey = normalizeMatchText(dto.reportKey);
    const label = normalizeMatchText(dto.label);
    const candidates = [
        ...(existingFolder?.reports || []),
        ...allExistingFolders.flatMap((folder) => folder.reports),
    ];

    return candidates.find((report) => (
        report.reportId === dto.reportId
        || (!!reportKey && normalizeMatchText(report.reportKey) === reportKey)
        || normalizeMatchText(report.label) === label
    ));
}

export function reportFromDto(makeReport: MakeReport, dto: ReportConfigurationReportDto, existing?: ReportConfig): ReportConfig {
    const report = existing ? clone(existing) : makeReport(reportUiId(dto.reportId), dto.label, [], false);
    return {
        ...report,
        id: reportUiId(dto.reportId),
        reportId: dto.reportId,
        reportFolderId: dto.reportFolderId,
        reportKey: dto.reportKey,
        sortOrder: dto.sortOrder,
        isBuiltIn: dto.isBuiltIn,
        label: dto.label,
        globalFilters: makeReportGlobalFilters(report.globalFilters),
    };
}

export function reportFolderFromDto(makeReport: MakeReport, dto: ReportConfigurationReportFolderDto, existingFolders: ReportFolder[]): ReportFolder {
    const existingFolder = findExistingFolderForDto(existingFolders, dto);
    return {
        ...(existingFolder ? clone(existingFolder) : { id: reportFolderUiId(dto.reportFolderId), label: dto.label, reports: [] }),
        id: reportFolderUiId(dto.reportFolderId),
        reportFolderId: dto.reportFolderId,
        reportPlanId: dto.reportPlanId,
        folderKey: dto.folderKey,
        sortOrder: dto.sortOrder,
        isBuiltIn: dto.isBuiltIn,
        label: dto.label,
        reports: dto.reports.map((reportDto) => reportFromDto(makeReport, reportDto, findExistingReportForDto(existingFolder, existingFolders, reportDto))),
    };
}

export function applyReportPlanToPlan(makeReport: MakeReport, plan: PlanConfig, reportPlan: ReportConfigurationReportPlanDto): PlanConfig {
    const existingFolders = plan.reportFolders || [];
    return {
        ...plan,
        reportPlanId: reportPlan.reportPlanId,
        planKey: reportPlan.planKey,
        planName: reportPlan.planName,
        clientId: reportPlan.clientId || plan.clientId,
        clientName: reportPlan.clientName || plan.clientName,
        reportFolders: reportPlan.folders.map((folder) => reportFolderFromDto(makeReport, folder, existingFolders)),
    };
}